import { cloneDeep } from "lodash-es";
import { DateTime } from "luxon";

import { goFetch } from "@/utils/network-utils";

import type { PersistedQueryPayload } from "@/types/api/fetch";
import type { SimplePages } from "@/types/api/content-models";
import type { SimplePageContent } from "@/types/entities";
import LocalForagePlugin, { simplePages, syncState } from "@/plugins/LocalForagePlugin";
import { simplePagesSyncKey } from "@/types/products/product-definitions";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export async function refreshSimplePagesFromServer(): Promise< SimplePageContent[] > {
    let allSimplePages: SimplePageContent[] = [];
    // Timing - testing to see how expensive each method is
    const t0: number = performance.now();

    let page: number = 0;
    const pageSize: number = 50;

    let simplePagesPayload: SimplePageContent[] = [];

    do {
        const response =
            await goFetch< SimplePages, PersistedQueryPayload >( {
                endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                payload: {
                    id: '61012e26c8fa503aac68cc591452ffcf',
                    variables: {
                        skip: page,
                        limit: pageSize
                    }
                }
            } );

        if ( response?.data?.simplePageCatalog ) {
            simplePagesPayload =
                [ ...( response.data.simplePageCatalog.simple_pages
                        .map( ( p: SimplePageContent ): SimplePageContent => ( {
                            id: p.id,
                            title: p.title,
                            slug: p.slug,
                            body: p.body
                        } ) )
                ) ];

            const t1: number = performance.now();
            logger.info( `Fetching & mapping ${ simplePagesPayload.length } ticker items took ${ t1 - t0 } ms` );

            allSimplePages = [ ...simplePagesPayload, ...allSimplePages ];
        }

        // update the page #
        page++;
    } while ( (simplePagesPayload?.length ?? 0) >= pageSize );

    const t1: number = performance.now();
    logger.info( `Fetching, mapping & persisting ${ allSimplePages.length } simple-page items took ${ t1 - t0 } ms` );

    return allSimplePages;
}

export async function refreshSimplePageCache(): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        if ( typeof simplePages === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( simplePagesSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {

            const nextSyncThresholdInMillis = DateTime.utc().plus( { hours: 4 } ).toMillis();
            await syncState.setItem( simplePagesSyncKey, nextSyncThresholdInMillis );

            // Retrieve the latest collection of simple pages from the content service
            const simplePagesReceived: SimplePageContent[] = await refreshSimplePagesFromServer();

            if(simplePagesReceived.length > 0)
            {
                updated = true;

                // Store the items recently retrieved
                await simplePages.ready();
                for ( const simplePage of simplePagesReceived ) {
                    await simplePages.setItem( simplePage.id, cloneDeep( simplePage ) );
                }

                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Loaded & cached ${ simplePagesReceived.length } simple pages from server in ${ t1 - t0 } ms` );
            }
            else
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( simplePagesSyncKey, nextSyncThresholdInMillis );
            }
        }
    } catch ( err: any ) {
        logger.error( JSON.stringify( err.message ) );
    }

    return updated;
}
